<script lang="ts" setup>
import { Delta } from "@vueup/vue-quill";
import throttle from "lodash.throttle";
import { storeToRefs } from "pinia";
import { useIsAnnotationCommentLong } from "~/composables/useIsAnnotationCommentLong";
import { useAnnotationFormState } from "~/stores/useAnnotationFormState";
import { RevisionAnnotation } from "~/types/RevisionAnnotation";

// @ts-ignore
import DOMPurify from "dompurify";

const props = defineProps({
  annotation: {
    type: Object as PropType<RevisionAnnotation>,
    required: true,
  },
  showTokenText: {
    type: Boolean,
    default: false,
  },
  useLines: {
    type: Boolean,
    default: true,
  },
});

const documentId = inject("documentId") as any;
const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(submittedDocumentStore);

const { setAnnotationBeingEdited, setAnnotationBeingInlineEdited } =
  useAnnotationFormState();

const { isThisAnnotationBeingEdited } = storeToRefs(useAnnotationFormState());

const documentRevisionParagraphStore =
  useDocumentRevisionParagraphsStore(documentId);
const { convertTokenGlobalPositionsToText } = storeToRefs(
  documentRevisionParagraphStore
);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { canEdit } = storeToRefs(documentViewModeStore);

const tokenText = computed(() => {
  return convertTokenGlobalPositionsToText.value(
    props.annotation.tokenGlobalPositions
  );
});

const shouldShowTokenText = computed(() => {
  return (
    props.showTokenText == true &&
    props.annotation.tokenGlobalPositions.length > 0 &&
    tokenText.value != ""
  );
});

const nuxtApp = useNuxtApp();

const annotationCommentLines = computed(() => {
  if (props.useLines != true) {
    // @ts-ignore
    return nuxtApp.$convertQuillDeltaToPlaintext(
      props.annotation.commentObject
    );
  }

  // @ts-ignore
  return nuxtApp.$convertQuillDeltaToLines(
    props.annotation.commentObject,
    false
  );
});

const annotationHTML = computed(() => {
  // @ts-ignore
  let html = nuxtApp.$convertQuillDeltaToHTML(props.annotation.commentObject);

  if (props.useLines != true) {
    html = (html as string)
      .replace(/<br\/>/g, "&nbsp;")
      .replace(/<p>/g, "<span>")
      .replace(/<\/p>/g, "</span>");
  }

  if (html == "") {
    return "&#8203;";
  } else {
    return html.replace(/[\u200B-\u200D\uFEFF]/g, "");
  }
});

const annotationFormStateStore = useAnnotationFormState();
const { isAnnotationBeingInlineEdited, annotationBeingInlineEdited } =
  storeToRefs(annotationFormStateStore);

const annotationCommentSpan = ref();
const { focused } = useFocus(annotationCommentSpan);

watch(annotationBeingInlineEdited, () => {
  setTimeout(() => {
    if (isEditable.value) {
      focused.value = true;

      var element = annotationCommentSpan.value as HTMLElement;

      const range = document.createRange();
      const sel = window.getSelection();

      if (!sel) return;

      if (!element || !element.childNodes) return;

      range.setStart(element, element.childNodes.length);
      range.setEnd(element, element.childNodes.length);

      // Clear any previous selections and set the new selection to the range
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, 100);
});

const isEditable = computed(() => {
  return isAnnotationBeingInlineEdited.value(props.annotation) == true;
});

const tabIndex = computed(() => {
  if (props.annotation.tokenGlobalPositions.length == 0) {
    return -1;
  }

  return (
    1000 +
    props.annotation.tokenGlobalPositions[
      props.annotation.tokenGlobalPositions.length - 1
    ]
  );
});

const saveAnnotationStore = useSaveAnnotation(documentId);
const documentSelectionStore = useDocumentSelectionStore(documentId);

const normalizeInlineAnnotationQuotes = (text: string) => {
  return text.replace(/[“”]/g, '"');
};

const isInlineDoubleQuoteKey = (event: KeyboardEvent) => {
  return event.key === '"' || event.key === "“" || event.key === "”";
};

const saveAndExitInline = async () => {
  if (isEditable.value != true) {
    return;
  }

  var commentSpan = annotationCommentSpan.value as HTMLElement | undefined;
  if (!commentSpan) return;
  setAnnotationBeingInlineEdited(documentId, undefined);

  document.getElementById("paragraphs-container")?.focus();

  var annotationText = normalizeInlineAnnotationQuotes(
    commentSpan.innerText,
  ).trim();

  annotationText = annotationText.replace(/[\u200B-\u200D\uFEFF]/g, "");

  if (annotationText == "") {
    await saveAnnotationStore.deleteAnnotation(props.annotation);

    if (props.annotation.tokenGlobalPositions.length > 0) {
      const endPosition =
        props.annotation.tokenGlobalPositions[
          props.annotation.tokenGlobalPositions.length - 1
        ];
      if (endPosition) {
        const newPosition = endPosition;
        useSelectTokenByPosition(newPosition);

        document.getElementById("paragraphs-container")?.focus();
      }
    }

    return;
  }

  props.annotation.commentObject = new Delta().insert(annotationText).ops;

  if (props.annotation.tokenGlobalPositions.length > 0) {
    documentSelectionStore.setSelection(
      props.annotation.tokenGlobalPositions[0],
      props.annotation.tokenGlobalPositions[
        props.annotation.tokenGlobalPositions.length - 1
      ]
    );
  }

  await saveAnnotationStore.saveAnnotation(props.annotation);

  // Removing this to see if it fixed soethign heidi was dealing with
  // if (endPosition) {
  //   const newPosition = endPosition + 1;
  //   useSelectTokenByPosition(newPosition);

  //   document.getElementById("paragraphs-container")?.focus();
  // }
};

const editAnnotation = () => {
  if (isEditable.value == true) {
    return;
  }

  setAnnotationBeingEdited(documentId, props.annotation);
};

// Hmm ok todo here.
const dblClick = async (ev: any) => {
  if (isThisAnnotationBeingEdited.value(props.annotation) == true) {
    return;
  }

  setAnnotationBeingInlineEdited(documentId, props.annotation);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (isInlineDoubleQuoteKey(event)) {
    event.preventDefault();
    insertAtCaret('"');
  } else if (event.key === "Æ" && event.altKey) {
    event.preventDefault();
    insertAtCaret('"');
  }

  if (event.key == "ArrowDown") {
    event.preventDefault();

    return;
  }

  if (event.key == "ArrowUp") {
    event.preventDefault();

    return;
  }
};

const userAnnotationsStore = useUserAnnotations();
const { annotationComments } = storeToRefs(userAnnotationsStore);

const possibleOptions = annotationComments;
const suggestions = ref<string[]>([]);
const suggestionIndex = ref<number | undefined>(undefined);

const handleKeyUp = (event: any) => {
  if (event.key == "ArrowDown") {
    event.preventDefault();

    suggestionIndex.value = Math.min(
      (suggestionIndex.value ?? 0) + 1,
      suggestions.value.length - 1
    );

    return;
  }

  if (event.key == "ArrowUp") {
    event.preventDefault();
    suggestionIndex.value = Math.max((suggestionIndex.value ?? 0) - 1, 0);
    return;
  }

  if (event.key == "Enter") {
    event.preventDefault();

    saveAndExitInline();

    return;
  }

  const innerText = event.target.innerText;
  findSuggestionsThottled(innerText);
};

const findSuggestions = (text: string) => {
  const possibleSuggestions = possibleOptions.value.filter((option) => {
    return option.toLowerCase().trim().includes(text.toLowerCase().trim());
  });

  if (suggestions.value.length != possibleSuggestions.length) {
    suggestionIndex.value = 0;
  }

  suggestions.value = possibleSuggestions;
};

const findSuggestionsThottled = throttle(findSuggestions, 100);

const onChooseSuggestion = (suggestion: string) => {
  annotationCommentSpan.value!.innerText =
    normalizeInlineAnnotationQuotes(suggestion);

  // move the selection to the end of the suggestion
  const range = document.createRange();
  const sel = window.getSelection();

  if (!sel) return;

  if (!annotationCommentSpan.value || !annotationCommentSpan.value.childNodes)
    return;

  range.setStart(
    annotationCommentSpan.value,
    annotationCommentSpan.value.childNodes.length
  );
  range.setEnd(
    annotationCommentSpan.value,
    annotationCommentSpan.value.childNodes.length
  );

  // Clear any previous selections and set the new selection to the range
  sel.removeAllRanges();
  sel.addRange(range);

  suggestions.value = [];
  suggestionIndex.value = 0;
};

const insertAtCaret = (forcedText?: string) => {
  const selection = window.getSelection();

  if (!selection) return;

  const range = selection.getRangeAt(0);
  range.deleteContents();

  const textToInsert = forcedText ?? '"';

  const node = document.createTextNode(textToInsert || "");
  range.insertNode(node);

  // Move caret after the inserted text
  range.setStartAfter(node);
  range.setEndAfter(node);
  selection.removeAllRanges();
  selection.addRange(range);
};

const appUserDataStore = useAppUserDataStore();
const { commentColor } = storeToRefs(appUserDataStore);

const draggingAnnotationStore = useDraggingAnnotation();
const { isAnnotationBeingDragged, selectedTokenGlobalPosition } = storeToRefs(
  draggingAnnotationStore
);

const onLongPress = (ev: MouseEvent) => {
  if (ev.buttons > 1) return;

  draggingAnnotationStore.setAnnotationBeingDragged(props.annotation);
};

const onLongPressRelease = () => {
  if (isAnnotationBeingDragged.value(props.annotation) == true) {
    draggingAnnotationStore.setAnnotationBeingDragged(undefined);

    const selectedPosition = selectedTokenGlobalPosition.value;

    draggingAnnotationStore.setSelectedTokenGlobalPosition(undefined);

    if (selectedPosition == undefined) return;

    documentSelectionStore.setSelection(selectedPosition, selectedPosition);
    props.annotation.tokenGlobalPositions = [selectedPosition];
    props.annotation.lastTokenGlobalPosition = selectedPosition;

    useSaveAnnotation(documentId).saveAnnotation(props.annotation);
  }
};

const documentZoomControlStore = useDocumentZoomControllStore(documentId);
const { zoomFactor } = storeToRefs(documentZoomControlStore);

const fontSize = computed(() => {
  return `${12 * zoomFactor.value}pt`;
});

const documentCommentColor = computed(() => {
  if (submittedDocument.value?.inkColor != undefined) {
    return submittedDocument.value.inkColor;
  }

  return commentColor.value;
});
</script>

<template>
  <DocumentAnnotationMenu
    @edit="editAnnotation"
    @delete="saveAnnotationStore.deleteAnnotation(annotation)"
    v-if="isEditable == true || useIsAnnotationCommentEmpty(annotation) != true"
    class="inline-annotation mx-[2px]"
    :data-step-id="`annotation-${annotation.id}`"
    :data-annotation-id="annotation.id"
    :style="{
      'scroll-margin-top': '50px',
      'scroll-margin-bottom': '50px',
    }"
    :disabled="canEdit != true"
    :class="{
      ' indent-0 inline-block': annotation.isStartLine == true,
    }"
  >
    <BaseClickHandler
      @click="editAnnotation"
      @dblclick="dblClick"
      @long-press="onLongPress"
      @release-long-press="onLongPressRelease"
      :style="{
        color: useArrayToRGB(documentCommentColor),
      }"
      :data-step-id="`annotation-click-handler-${annotation.id}`"
      :disabled="canEdit != true"
      class="relative"
      :class="{
        'hover:bg-light-default cursor-pointer selection:bg-none':
          isEditable != true,
        ' bg-highlight-selected hover:bg-highlight-selected  hover:bg-opacity-40  bg-opacity-40 rounded select-none border-2 border-highlight-selected':
          isAnnotationBeingDragged(annotation),
        ' indent-0 inline-block pr-1': annotation.isStartLine == true,
      }"
    >
      <DocumentAnnotationDeductionTooltipWrapper
        :key="annotation.displayOrder"
        :annotation="annotation"
        :disabled="isEditable || canEdit != true"
        :class="{
          'flex flex-col':
            isEditable != true &&
            annotationCommentLines.length > 1 &&
            useLines == true,
        }"
      >
        <span v-if="shouldShowTokenText" class="font-bold">
          {{ tokenText }} -
        </span>

        <span
          v-if="showTokenText != true && useIsAnnotationCommentLong(annotation)"
          class=""
        >
          See below.
        </span>
        <span
          v-else
          @blur="saveAndExitInline"
          @keyup.esc.prevent.stop="saveAndExitInline"
          @keydown.enter.prevent.stop
          :key="annotation.commentObject"
          :contenteditable="isEditable"
          :tabindex="tabIndex"
          ref="annotationCommentSpan"
          class="pl-[4px]"
          :class="{
            'px-[4px] py-[5px] leading-[12pt]  rounded border  outline-none focus:outline-none':
              isEditable,
            'flex flex-col':
              isEditable != true &&
              annotationCommentLines.length > 1 &&
              useLines == true,
          }"
          :style="{
            'border-color': useArrayToRGB(documentCommentColor),
            color: useArrayToRGB(documentCommentColor),
            'font-size': fontSize,
          }"
          @keydown="handleKeydown"
          @keyup.prevent.stop="handleKeyUp"
        >
          <span
            v-html="DOMPurify.sanitize(annotationHTML)"
            class="annotation-html-span leading-6 indent-0"
          ></span>
        </span>
      </DocumentAnnotationDeductionTooltipWrapper>
    </BaseClickHandler>
  </DocumentAnnotationMenu>
</template>

<style>
.inline-annotation.highlight {
  @apply bg-highlight-selected;
  @apply bg-opacity-60;
}

.annotation-html-span p {
  display: inline;
}
</style>
